import { useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import AuthShell, { AuthDivider } from '../../components/AuthShell/AuthShell';
import SocialAuthButtons from '../../components/AuthShell/SocialAuthButtons';
import { reconnectIdentity } from '../../services/authService';
import styles from '../../components/AuthShell/authForm.module.css';

export default function Login() {
    const { login, loginWithGoogle, loginWithApple, restoreState, restoreAccount } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const fromState = location.state?.from;
    const from = fromState?.pathname
        ? `${fromState.pathname || '/'}${fromState.search || ''}${fromState.hash || ''}`
        : '/';

    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [restoreError, setRestoreError] = useState('');
    const emailMethodTracked = useRef(false);
    // Google/Apple sign-ins the server asks the owner to reconnect after this
    // password sign-in (disconnected by account recovery or a security review).
    const [reconnect, setReconnect] = useState(null); // { providers, password }
    const [reconnectError, setReconnectError] = useState('');

    // AuthContext already navigated when a gate applies (DOB, onboarding,
    // pending quiz); only fall back to `from` when it returned no destination.
    const finish = (outcome) => {
        if (outcome?.deleted) return;
        if (!outcome?.destination) navigate(from, { replace: true });
    };

    const handleGoogleCredential = async (credential) => {
        setErrors({});
        finish(await loginWithGoogle(credential));
    };

    const handleAppleIdentity = async (identity) => {
        setErrors({});
        finish(await loginWithApple(identity));
    };

    const validate = () => {
        const e = {};
        if (!identifier.trim()) e.identifier = 'Email or username is required.';
        if (!password) e.password = 'Password is required.';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!emailMethodTracked.current) {
            emailMethodTracked.current = true;
            trackEvent(ANALYTICS_EVENTS.AUTH_METHOD_SELECTED, { platform: 'web', mode: 'login', method: 'email' });
        }
        if (!validate()) return;
        setLoading(true);
        try {
            const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier.trim());
            const outcome = await login({
                ...(isEmail ? { email: identifier.trim() } : { username: identifier.trim() }),
                password,
            });
            const providers = outcome?.payload?.identitiesNeedingReconnect || [];
            if (!outcome?.deleted && !outcome?.destination && providers.length) {
                setReconnect({ providers, password });
                return;
            }
            finish(outcome);
        } catch (err) {
            setErrors({ form: err.message || 'Login failed. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const reconnectWith = async (provider, token) => {
        setReconnectError('');
        await reconnectIdentity({ provider, token, currentPassword: reconnect.password });
        const remaining = reconnect.providers.filter((p) => p !== provider);
        if (remaining.length) setReconnect({ ...reconnect, providers: remaining });
        else navigate(from, { replace: true });
    };

    const handleRestore = async () => {
        setRestoring(true);
        setRestoreError('');
        try {
            await restoreAccount();
            navigate(from, { replace: true });
        } catch (err) {
            setRestoreError(err.message || 'Restore failed. Please try again.');
        } finally {
            setRestoring(false);
        }
    };

    const footer = (
        <>
            <p className={styles.legalText}>
                By signing in, you agree to our{' '}
                <Link to="/terms" className={styles.legalLink}>Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className={styles.legalLink}>Privacy Policy</Link>.
            </p>
            <p className={styles.switchText}>
                New to Aluvo?{' '}
                <Link to="/register" state={location.state} className={styles.link}>Create account</Link>
            </p>
        </>
    );

    return (
        <AuthShell
            mode="login"
            title="Welcome back"
            subtitle="Sign in to pick up your collection where you left off."
            pageTitle="Sign in · Aluvo"
            footer={footer}
            clarityMask
        >
            {restoreState.visible && (
                <div className={styles.restoreBanner} role="alert">
                    <p className={styles.restoreText}>
                        ⚠️ This account was deleted. Would you like to restore it?
                    </p>
                    {restoreError && <p className={styles.errorMsg}>{restoreError}</p>}
                    <div className={styles.restoreActions}>
                        <button type="button" className={styles.btnPrimary} onClick={handleRestore} disabled={restoring}>
                            {restoring ? <span className={styles.spinner} aria-label="Restoring" /> : 'Restore Account'}
                        </button>
                    </div>
                </div>
            )}

            {reconnect && (
                <div className={styles.restoreBanner} role="region" aria-label="Reconnect sign-in methods">
                    <p className={styles.restoreText}>
                        You're signed in. For your security, Sign in with{' '}
                        {reconnect.providers.map((p) => (p === 'apple' ? 'Apple' : 'Google')).join(' and ')}{' '}
                        was disconnected from this account. Reconnect it below to keep using it, or skip for now.
                    </p>
                    {reconnectError && <p className={styles.errorMsg}>{reconnectError}</p>}
                    <SocialAuthButtons
                        mode="login"
                        onGoogleCredential={(credential) => reconnectWith('google', credential)}
                        onAppleIdentity={(identity) => reconnectWith('apple', identity.identityToken)}
                        onError={(message) => setReconnectError(message)}
                    />
                    <div className={styles.restoreActions}>
                        <button type="button" className={styles.btnPrimary} onClick={() => navigate(from, { replace: true })}>
                            Skip for now
                        </button>
                    </div>
                </div>
            )}

            {!reconnect && (
            <SocialAuthButtons
                mode="login"
                onGoogleCredential={handleGoogleCredential}
                onAppleIdentity={handleAppleIdentity}
                onError={(message) => setErrors({ form: message })}
                disabled={loading}
            />
            )}

            <AuthDivider label="or continue with email" />

            <form className={styles.form} onSubmit={handleLogin} noValidate aria-label="Sign in with email">
                <div className={styles.field}>
                    <label htmlFor="login-identifier" className={styles.srOnly}>Email or username</label>
                    <input
                        id="login-identifier"
                        className={`${styles.input} ${errors.identifier ? styles.inputError : ''}`}
                        type="text"
                        placeholder="Email or username"
                        value={identifier}
                        onChange={(e) => { setIdentifier(e.target.value); setErrors(p => ({ ...p, identifier: undefined })); }}
                        autoComplete="username"
                        autoCapitalize="none"
                        aria-invalid={errors.identifier ? 'true' : undefined}
                        aria-describedby={errors.identifier ? 'login-identifier-error' : undefined}
                    />
                    {errors.identifier && (
                        <span id="login-identifier-error" className={styles.fieldError} role="alert">{errors.identifier}</span>
                    )}
                </div>

                <div className={styles.field}>
                    <label htmlFor="login-password" className={styles.srOnly}>Password</label>
                    <div className={`${styles.passwordWrap} ${errors.password ? styles.inputError : ''}`}>
                        <input
                            id="login-password"
                            className={styles.passwordInput}
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Password"
                            value={password}
                            onChange={(e) => { setPassword(e.target.value); setErrors(p => ({ ...p, password: undefined })); }}
                            autoComplete="current-password"
                            aria-invalid={errors.password ? 'true' : undefined}
                            aria-describedby={errors.password ? 'login-password-error' : undefined}
                        />
                        <button
                            type="button"
                            className={styles.eyeBtn}
                            onClick={() => setShowPassword(s => !s)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            aria-pressed={showPassword}
                        >
                            {showPassword ? '🙈' : '👁️'}
                        </button>
                    </div>
                    {errors.password && (
                        <span id="login-password-error" className={styles.fieldError} role="alert">{errors.password}</span>
                    )}
                </div>

                <p className={styles.rowEnd}>
                    <Link to="/forgot-password" className={styles.linkMuted}>Forgot password?</Link>
                </p>

                {errors.form && <p className={styles.errorMsg} role="alert">{errors.form}</p>}

                <button className={styles.btnPrimary} type="submit" disabled={loading} aria-busy={loading || undefined}>
                    {loading ? <span className={styles.spinner} aria-label="Signing in" /> : 'Sign In'}
                </button>
            </form>
        </AuthShell>
    );
}
